// Sign-in and identity: Supabase Discord OAuth, or a typed name when the server has DEV_AUTH on.

import { create } from "zustand";
import { supabase, supabaseConfigured } from "../lib/supabase.js";

export type Profile = { id: string; displayName: string; discordUsername: string };

/** An onboarded Discord server you're a member of. */
export type Guild = { id: string; name: string; iconUrl: string | null; canStoryteller: boolean };

export type ServerConfig = {
  devAuth: boolean;
  supabaseAuth: boolean;
};

const DEV_TOKEN_KEY = "cc:dev-token";

type AuthState = {
  status: "loading" | "signed_out" | "signed_in" | "denied";
  config: ServerConfig | null;
  token: string | null;
  profile: Profile | null;
  /** The onboarded servers you're in; games are confined to one of these. */
  guilds: Guild[];
  /** A Storyteller in at least one of them, so you can host. */
  canStoryteller: boolean;
  isAdmin: boolean;
  error: string | null;
  init: () => Promise<void>;
  signInDev: (name: string) => Promise<void>;
  signOut: () => Promise<void>;
  setDisplayName: (name: string) => Promise<void>;
};

async function fetchConfig(): Promise<ServerConfig> {
  const res = await fetch("/api/config");
  if (!res.ok) throw new Error("The server is not answering.");
  return (await res.json()) as ServerConfig;
}

async function currentToken(): Promise<string | null> {
  if (supabaseConfigured) {
    const { data } = await supabase!.auth.getSession();
    if (data.session?.access_token) return data.session.access_token;
  }
  return localStorage.getItem(DEV_TOKEN_KEY);
}

export const useAuth = create<AuthState>((set, get) => {
  // The session arrives after the OAuth redirect, so re-init on auth events. Subscribe once here,
  // not in init(), or each call adds another listener.
  if (supabaseConfigured) {
    supabase!.auth.onAuthStateChange(() => void get().init());
  }

  return {
  status: "loading",
  config: null,
  token: null,
  profile: null,
  guilds: [],
  canStoryteller: false,
  isAdmin: false,
  error: null,

  init: async () => {
    try {
      const config = await fetchConfig();
      set({ config });

      const token = await currentToken();
      if (!token) {
        set({ status: "signed_out", token: null, profile: null });
        return;
      }

      const res = await fetch("/api/session", {
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        // 403 is "you are not in a participating Discord server", which is a dead end rather than a retry.
        set({
          status: res.status === 403 ? "denied" : "signed_out",
          error: body.error ?? "Could not sign you in.",
          token: null,
          profile: null,
        });
        if (res.status !== 403) localStorage.removeItem(DEV_TOKEN_KEY);
        return;
      }
      const body = (await res.json()) as { profile: Profile; isAdmin: boolean; guilds: Guild[] };
      set({
        status: "signed_in",
        token,
        profile: body.profile,
        guilds: body.guilds,
        canStoryteller: body.guilds.some((g) => g.canStoryteller),
        isAdmin: body.isAdmin,
        error: null,
      });
    } catch (err) {
      set({ status: "signed_out", error: err instanceof Error ? err.message : "Something went wrong." });
    }
  },

  signInDev: async (name: string) => {
    localStorage.setItem(DEV_TOKEN_KEY, `dev:${name.trim()}`);
    await get().init();
  },

  signOut: async () => {
    localStorage.removeItem(DEV_TOKEN_KEY);
    if (supabaseConfigured) await supabase!.auth.signOut();
    set({ status: "signed_out", token: null, profile: null, guilds: [], canStoryteller: false, isAdmin: false });
  },

  setDisplayName: async (displayName: string) => {
    const { token } = get();
    if (!token) return;
    const res = await fetch("/api/profile/display-name", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ displayName }),
    });
    const body = (await res.json().catch(() => ({}))) as { profile?: Profile; error?: string };
    if (!res.ok || !body.profile) throw new Error(body.error ?? "Could not save that name.");
    set({ profile: body.profile });
  },
  };
});
